import { sectionRegistry } from '../canvas/sectionRegistry';
import { Fragment, useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { isReadOnlyView } from '../boot/view-handoff';
import { useBuilderStore } from '../state/builderStore';
import {
  CHROME_SECTION_TYPES,
  FOOTER_SECTION_TYPES,
  HEADER_SECTION_TYPES,
} from '../state/sectionZones';
import { bt, btp } from '../i18n';
import { blockDisplayName, sectionDisplayName } from './sectionVariantLabel';
import { useChromeStore } from '../state/chromeStore';
import { buildAnnouncements } from '../canvas/dnd/announcements';
import { AddSectionMenu } from './AddSectionMenu';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
  blockTypeIcon,
  sectionTypeIcon,
} from '../ui/icons';
import { AddPopover, type AddPopoverGroup } from './AddPopover';
import { ThemeSettingsPanel } from '../theme/ThemeSettingsPanel';
import { wearsStoreChrome } from '@aquilixshop/ui-react';
import type { Block, SectionDefinition, SectionInstance } from '@aquilixshop/ui-react';

/**
 * Left sidebar (build-plan §7.4) — a ~320px Polaris panel that switches content with the topbar
 * icon-rail (chrome feature 1, via `chromeStore.sidebarTab`):
 *
 *   - "sections" → the section TREE grouped into HEADER (site-wide) · TEMPLATE (page sections, with the
 *     Add-section button) · FOOTER (site-wide), with dividers (chrome feature 2). Header/Footer collapse
 *     by default. Each section row: drag-handle + type icon + name, hover eye/trash, chevron → blocks.
 *   - "theme"    → the grouped global theme-setting list (Colors / Typography / Layout / …) — stubbed
 *     content, real grouped list.
 *
 * Reads the SAME engine `order[]` + `sectionsById` (one model, two views); reorder/structural edits go
 * through the unchanged store reducers.
 */
/*
 * Site-wide CHROME section types — they live in the page `order` but render in the rail's HEADER / FOOTER
 * groups (Shopify parity), NOT in the Template list, and are excluded from the Template "Add section".
 * The lists live in `state/sectionZones` because the STORE enforces the same partition (a move can't
 * leave its zone; a loaded document is repaired) — the rail's grouping is only honest if it does.
 */

/**
 * May a section be ADDED to this page? (05/10/2026)
 *
 * The picker used to offer every non-chrome section on every page: the order confirmation on the home
 * page, and — the day the checkout became a template — a slideshow on the checkout. The storefront's
 * checkout reads ONE section of its document (the `checkout` section's look) and paints nothing else, so
 * anything a seller added around it in the editor would exist in the builder and nowhere a buyer goes.
 *
 * Two rules, the smallest that close that gap:
 *  - the checkout page is STRICT: only a section that names `checkout` in its `templates` is offered;
 *  - a ONE-PER-PAGE section (`order-confirmation`, `checkout`) is offered on its own page only. Every
 *    other section keeps today's reach, whatever its `templates` list says — a product gallery on the
 *    home page is a choice the picker has always allowed, and the AI composer is the one reader of those
 *    lists; this rail is not the place to re-litigate what they were written for.
 */
/**
 * Sections a page carries ONCE: a second checkout would ask the buyer to pay twice, a second order
 * confirmation would show the order twice. Their schemas say so in words; the picker has to say it too.
 */
const ONE_PER_PAGE: ReadonlySet<string> = new Set([
  'checkout',
  'order-confirmation',
  'welcome-popup',
]);

export function offeredOn(type: string, template: string | undefined): boolean {
  const templates = sectionRegistry[type]?.schema.templates as readonly string[] | undefined;
  if (template === 'checkout') return !!templates?.includes('checkout');
  // La FENÊTRE DE BIENVENUE est stricte comme la page de paiement (feature 0074, 18/10/2026) : elle ne
  // prend QUE les sections qui se déclarent pour elle. Une carte de 420 px n'a rien à faire d'un
  // diaporama ou d'une collection en vedette, et les soixante sections de la vitrine dans son menu
  // rendraient introuvables les trois qu'elle peut vraiment porter.
  if (template === 'welcome-popup') return !!templates?.includes('welcome-popup');
  if (
    ONE_PER_PAGE.has(type) &&
    templates !== undefined &&
    template !== undefined &&
    !templates.includes(template)
  ) {
    return false;
  }
  return true;
}

export function SectionRail() {
  const order = useBuilderStore((s) => s.order);
  const template = useBuilderStore((s) => s.route.template);
  const moveSection = useBuilderStore((s) => s.moveSection);
  const sidebarTab = useChromeStore((s) => s.sidebarTab);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const labelFor = (id: UniqueIdentifier) => {
    const sec = sectionsById[String(id)];
    return sectionDisplayName(sec);
  };
  const positionOf = (id: UniqueIdentifier) => order.indexOf(String(id)) + 1;

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const to = order.indexOf(String(over.id));
    if (to !== -1) moveSection(String(active.id), to);
  }

  // Partition the page `order` by role (Shopify-style grouping): chrome header sections render in the
  // HEADER group, footer in the FOOTER group, everything else in TEMPLATE. They all stay real, selectable
  // sections in `order`; only the RAIL presentation + the "Add section" filtering differ.
  const typeOf = (id: string): string => sectionsById[id]?.type ?? '';
  const headerIds = order.filter((id) => HEADER_SECTION_TYPES.includes(typeOf(id)));
  const footerIds = order.filter((id) => FOOTER_SECTION_TYPES.includes(typeOf(id)));
  const templateIds = order.filter((id) => !CHROME_SECTION_TYPES.includes(typeOf(id)));
  // Where a new section appends within each group's global `order` range.
  const footerStart = footerIds.length ? order.indexOf(footerIds[0]!) : order.length;
  const headerEnd = headerIds.length ? order.indexOf(headerIds[headerIds.length - 1]!) + 1 : 0;
  const announce: ComponentProps<typeof DndContext>['accessibility'] = {
    announcements: buildAnnouncements(labelFor, positionOf, () => order.length, 'section'),
  };
  const notPresent = (t: string, ids: string[]) => !ids.some((id) => typeOf(id) === t);
  // What the body of THIS page may receive: never the chrome, never a section that belongs to one other
  // page (see `offeredOn`), and never a SECOND copy of a section the page can only carry once.
  const bodyOffer = (t: string) =>
    !CHROME_SECTION_TYPES.includes(t) &&
    offeredOn(t, template) &&
    (!ONE_PER_PAGE.has(t) || notPresent(t, templateIds));
  const headerAddable = HEADER_SECTION_TYPES.some((t) => notPresent(t, headerIds));
  const footerAddable = FOOTER_SECTION_TYPES.some((t) => notPresent(t, footerIds));
  // La fenêtre de bienvenue ne porte pas le chrome de la boutique : elle est peinte PAR-DESSUS une page
  // qui l'a déjà. Montrer deux groupes vides avec un bouton « Ajouter une section d'en-tête » inviterait
  // à poser un en-tête dans une carte de 440 px — et le document n'a nulle part où le ranger.
  const withChrome = wearsStoreChrome(template ?? 'index');

  return (
    <nav className="bld-rail" aria-label={bt('chrome.rail.aria', 'Theme')}>
      {sidebarTab === 'sections' && (
        <div className="bld-rail__list">
          {withChrome && (
            <RailSectionGroup
              title={bt('chrome.rail.header', 'Header')}
              ids={headerIds}
              sensors={sensors}
              onDragEnd={onDragEnd}
              announce={announce}
              addMenu={
                headerAddable ? (
                  <AddSectionMenu
                    atIndex={headerEnd}
                    triggerAriaLabel={bt('chrome.rail.add_header', 'Add a header section')}
                    filter={(t) => HEADER_SECTION_TYPES.includes(t) && notPresent(t, headerIds)}
                  />
                ) : null
              }
            />
          )}
          <RailSectionGroup
            title={bt('chrome.rail.template', 'Template')}
            ids={templateIds}
            sensors={sensors}
            onDragEnd={onDragEnd}
            announce={announce}
            inserts={{
              atIndexOf: (i) => order.indexOf(templateIds[i]!),
              filter: bodyOffer,
            }}
            addMenu={<AddSectionMenu atIndex={footerStart} filter={bodyOffer} />}
          />
          {withChrome && (
            <RailSectionGroup
              title={bt('chrome.rail.footer', 'Footer')}
              ids={footerIds}
              sensors={sensors}
              onDragEnd={onDragEnd}
              announce={announce}
              addMenu={
                footerAddable ? (
                  <AddSectionMenu
                    atIndex={order.length}
                    triggerAriaLabel={bt('chrome.rail.add_footer', 'Add a footer section')}
                    filter={(t) => FOOTER_SECTION_TYPES.includes(t) && notPresent(t, footerIds)}
                  />
                ) : null
              }
            />
          )}
        </div>
      )}

      {sidebarTab === 'theme' && <ThemeSettingsPanel />}
    </nav>
  );
}

/**
 * One rail SECTION GROUP (Header / Template / Footer) — a labelled box with its OWN DndContext +
 * SortableContext so reordering is constrained WITHIN the group (you can't drag a header section into the
 * template). The Template group also renders a "+" insert before each row; every group ends with its own
 * filtered "Add section". Rows are the same selectable {@link SectionRow} as before.
 */
function RailSectionGroup({
  title,
  ids,
  sensors,
  onDragEnd,
  announce,
  inserts,
  addMenu,
}: {
  title: string;
  ids: string[];
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (e: DragEndEvent) => void;
  announce?: ComponentProps<typeof DndContext>['accessibility'];
  inserts?: { atIndexOf: (i: number) => number; filter: (t: string) => boolean };
  addMenu?: ReactNode;
}) {
  // Superadmin consultation: the between-rows "+" is a PURE add affordance — there is nothing to read
  // on it. Disabling it was not enough either: `.bld-railinsert__btn:disabled { opacity: .35 }` beats the
  // base `opacity: 0`, so a consultation PAINTED an insert line on every gap — more "+" than the editor
  // itself shows (founder 25/10/2026). Not rendered at all here.
  const readOnly = isReadOnlyView();
  return (
    <div className="bld-railgroup">
      <div className="bld-railgroup__head bld-railgroup__head--static">
        <span className="bld-railgroup__title">{title}</span>
      </div>
      {ids.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={onDragEnd}
          accessibility={announce}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ul role="list" className="bld-rail__items">
              {ids.map((id, i) => (
                <Fragment key={id}>
                  {inserts && !readOnly ? (
                    <li className="bld-railinsert">
                      <AddSectionMenu
                        atIndex={inserts.atIndexOf(i)}
                        label=""
                        className="bld-railinsert__btn"
                        triggerAriaLabel={bt('chrome.rail.add_here', 'Add a section here')}
                        filter={inserts.filter}
                      />
                    </li>
                  ) : null}
                  <SectionRow id={id} />
                </Fragment>
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
      {addMenu}
    </div>
  );
}

function SectionRow({ id }: { id: string }) {
  const section = useBuilderStore((s) => s.sectionsById[id]);
  const selected = useBuilderStore((s) => s.selectedSectionId === id);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const requestCanvasReveal = useBuilderStore((s) => s.requestCanvasReveal);
  const moveSection = useBuilderStore((s) => s.moveSection);
  const removeSection = useBuilderStore((s) => s.removeSection);
  const duplicateSection = useBuilderStore((s) => s.duplicateSection);
  const toggleSectionDisabled = useBuilderStore((s) => s.toggleSectionDisabled);
  const order = useBuilderStore((s) => s.order);
  const readOnly = isReadOnlyView();

  const sortable = useSortable({ id, disabled: readOnly });
  const [expanded, setExpanded] = useState(false);

  const def = section ? sectionRegistry[section.type] : undefined;
  const blockSchemas = def?.schema.blocks ?? [];
  const hasBlocks = blockSchemas.length > 0;

  // Auto-expand the block tree when this section becomes the selected one (Shopify behaviour).
  useEffect(() => {
    if (selected && hasBlocks) setExpanded(true);
  }, [selected, hasBlocks]);

  if (!section) return null;
  const label = sectionDisplayName(section);
  const suffix = sectionContentSuffix(section);
  // The row's action buttons announce the heading too ("Hide Testimonials — Ils nous font confiance"),
  // so two same-type sections stay distinguishable by voice, not just visually.
  const rowLabel = suffix ? `${label} — ${suffix}` : label;
  const hidden = !!section.disabled;
  const index = order.indexOf(id);
  const showBlocks = hasBlocks && expanded;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  // Accessible name carries the hidden state ("Hero, hidden") per build-plan §7.4.
  const accName = hidden ? `${label}, hidden` : label;

  return (
    <li ref={sortable.setNodeRef} style={style}>
      {/* The WHOLE row is the drag handle — no separate grip icon (founder 2026-07-04): the pointer
          `listeners` sit on the row container, so a seller grabs anywhere on the row to reorder, while a
          click (no movement) still selects / toggles via the inner buttons (dnd-kit's 6px distance lets
          clicks through). Keyboard reorder stays available through the "⋯" Move up/down menu. */}
      <div
        className={selected ? 'bld-sec is-selected' : 'bld-sec'}
        data-rail-section-id={id}
        {...sortable.listeners}
        title={
          readOnly
            ? bt('chrome.view.readonly', 'Read-only consultation')
            : bt('chrome.rail.drag', 'Drag to reorder')
        }
      >
        {hasBlocks ? (
          <button
            type="button"
            className="bld-sec__chev dir-flip"
            aria-label={expanded ? `Collapse ${rowLabel} blocks` : `Expand ${rowLabel} blocks`}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
          </button>
        ) : (
          <span className="bld-sec__chev-spacer" aria-hidden="true" />
        )}
        <button
          type="button"
          className="bld-sec__main"
          aria-current={selected ? 'true' : undefined}
          onClick={() => {
            // Select the section AND ask the canvas to scroll it into view (Shopify parity): picking a
            // section in the tree jumps the preview to it, even when you were scrolled at the footer.
            selectSection(id);
            requestCanvasReveal();
          }}
        >
          <span className={hidden ? 'bld-sec__type is-hidden' : 'bld-sec__type'} aria-hidden="true">
            {sectionTypeIcon(def?.meta.icon, 16)}
          </span>
          <span className="bld-sec__name">{accName}</span>
          {suffix && (
            <span className="bld-sec__suffix" aria-hidden="true">
              {suffix}
            </span>
          )}
        </button>
        <div className="bld-sec__actions">
          <button
            type="button"
            className="bld-sec__act"
            aria-label={hidden ? `Show ${rowLabel}` : `Hide ${rowLabel}`}
            aria-pressed={hidden}
            disabled={readOnly}
            title={
              readOnly
                ? bt('chrome.view.readonly', 'Read-only consultation')
                : hidden
                  ? bt('chrome.sec.show', 'Show section')
                  : bt('chrome.sec.hide', 'Hide section')
            }
            onClick={() => toggleSectionDisabled(id)}
          >
            {hidden ? <EyeOffIcon size={15} /> : <EyeIcon size={15} />}
          </button>
          <button
            type="button"
            className="bld-sec__act bld-sec__act--danger"
            aria-label={btp('chrome.sec.remove_named', 'Remove {name}', { name: rowLabel })}
            disabled={readOnly}
            title={
              readOnly
                ? bt('chrome.view.readonly', 'Read-only consultation')
                : bt('chrome.sec.remove', 'Remove section')
            }
            onClick={() => removeSection(id)}
          >
            <TrashIcon size={15} />
          </button>
          <RowActionsMenu
            label={label}
            isFirst={index === 0}
            isLast={index === order.length - 1}
            hidden={hidden}
            disabled={readOnly}
            onMoveUp={() => moveSection(id, index - 1)}
            onMoveDown={() => moveSection(id, index + 1)}
            onToggleHidden={() => toggleSectionDisabled(id)}
            onDuplicate={() => duplicateSection(id)}
          />
        </div>
      </div>
      {showBlocks && <RailBlockTree section={section} />}
    </li>
  );
}

// ── recursive block-tree helpers (mirror the store's Shrine accepts/static model) ─────────────────
type Def = SectionDefinition | undefined;

/** Child types a SECTION accepts (explicit `accepts`, else legacy = all its declared block types). */
function acceptsForSection(def: Def): string[] {
  return def?.schema.accepts
    ? [...def.schema.accepts]
    : (def?.schema.blocks?.map((b) => b.type) ?? []);
}

/** Child types a BLOCK accepts (its schema's `accepts`; empty = a leaf). */
function acceptsForBlockType(def: Def, type: string): string[] {
  const bs = def?.schema.blocks?.find((b) => b.type === type);
  return bs?.accepts ? [...bs.accepts] : [];
}

/** Theme-fixed static-child types of a section (Gallery/Details) — never offered in "Add block". */
function staticChildTypes(def: Def): string[] {
  return def?.schema.staticChildren?.map((c) => c.type) ?? [];
}

/** Find a block anywhere in a tree by id. */
function findBlockDeep(blocks: Block[], id: string): Block | null {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.blocks) {
      const f = findBlockDeep(b.blocks, id);
      if (f) return f;
    }
  }
  return null;
}

/** Total blocks in a tree (for the section-wide cap). */
function countBlocksDeep(blocks: Block[]): number {
  let n = 0;
  for (const b of blocks) {
    n += 1;
    if (b.blocks) n += countBlocksDeep(b.blocks);
  }
  return n;
}

/**
 * Locate a block's SIBLING list (the array it lives in), its parent block id, and its index there — by
 * walking the whole tree. Used by the single tree-wide DndContext to reorder a dragged block WITHIN its
 * own level, and by each row's Move up/down menu. Returns null if the id isn't found.
 */
function findSiblingsDeep(
  blocks: Block[],
  id: string,
  parentId: string | null = null,
): { siblings: Block[]; parentId: string | null; index: number } | null {
  const index = blocks.findIndex((b) => b.id === id);
  if (index !== -1) return { siblings: blocks, parentId, index };
  for (const b of blocks) {
    if (b.blocks) {
      const found = findSiblingsDeep(b.blocks, id, b.id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Every REORDERABLE (non-static) block id across the whole tree, in DOM order (a block's row, then its
 * children). One FLAT SortableContext over these ids — structurally identical to the section list that
 * already drags reliably — avoids the nested-SortableContext setup that broke block dragging. Cross-level
 * drops are filtered out in onDragEnd (same-parent only).
 */
function flattenReorderableIds(blocks: Block[]): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    if (!b.static) out.push(b.id);
    if (b.blocks) out.push(...flattenReorderableIds(b.blocks));
  }
  return out;
}

/**
 * Plain text out of a settings value that may be a rich-text object (`{ format: 'html', html }`) or a
 * bare string. Tags are stripped and the handful of entities our editor emits are decoded — this feeds
 * a 24-char rail label, so a regex beats spinning up a DOM parser per row.
 */
function settingPlainText(raw: unknown): string {
  const html =
    typeof raw === 'string'
      ? raw
      : raw && typeof raw === 'object' && (raw as { format?: string }).format === 'html'
        ? String((raw as { html?: unknown }).html ?? '')
        : '';
  if (!html) return '';
  return (
    html
      .replace(/<[^>]*>/g, ' ')
      // Some seeded headings are plain strings carrying markdown emphasis ("des avantages **concrets**") —
      // the rail shows words, not syntax.
      .replace(/(\*\*|__|\*|_)/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * A short italic content preview shown after a SECTION's name in the rail (its heading, mostly).
 *
 * Why: the rail label is the section type's `meta.label`, so two `testimonials` on the same page read as
 * two identical "Testimonials" rows even when one is a quote grid and the other a `media-overlay` video
 * card set — nothing in the tree told them apart (founder 2026-08-01). Shopify solves this by surfacing
 * the section's own heading; so do we, for every section type at once rather than special-casing one.
 */
function sectionContentSuffix(section: SectionInstance): string | null {
  const s = (section.settings ?? {}) as Record<string, unknown>;
  for (const key of ['heading', 'title', 'subtitle', 'text', 'label']) {
    const t = settingPlainText(s[key]);
    if (t) return t.length > 24 ? `${t.slice(0, 24)}…` : t;
  }
  return null;
}

/** A short italic content preview shown after a block's name in the rail (e.g. a trust line's text). */
function blockContentSuffix(block: Block): string | null {
  // A container block (e.g. main-product's static Gallery/Details) can carry no `settings` — guard it so
  // a settings-less block never throws and blanks the whole rail (and with it the builder).
  const s = (block.settings ?? {}) as Record<string, unknown>;
  // `c1` = a size-row's first cell, so table rows read "Table row — Head size" instead of N
  // indistinguishable "Table row" entries.
  // `btnLabel` is the SAME problem one type over: a tab stores its name there (content-tabs and
  // tabbed-columns both), so a two-tab section read as two identical "Onglet" rows with nothing to tell
  // the seller which one she was about to edit (found 18/09/2026 walking the rail).
  //
  // FALL THROUGH, and read RICH TEXT. This used to take `s.text ?? s.heading ?? …` in one shot and then
  // bail on anything that was not a string — so a block whose `text` is a richtext OBJECT (the normal
  // case: every Multicolumn column, every tab column) stopped at the first key and returned null, and the
  // row lost the title it did have. Each key is now tried in turn, through the same `settingPlainText`
  // the SECTION suffix has always used, which unwraps `{format:'html', html}` and strips the tags.
  for (const key of ['text', 'heading', 'title', 'label', 'btnLabel', 'ctaLabel', 'c1']) {
    const t = settingPlainText(s[key]).trim();
    if (t) return t.length > 24 ? `${t.slice(0, 24)}…` : t;
  }
  return null;
}

/**
 * The LEFT-rail block tree (Shopify/Shrine parity) — now RECURSIVE: it walks the section's real nested
 * block tree (main-product → Gallery + Details → buy-box blocks → …). Each node is selectable (opens its
 * inspector), a CONTAINER node (one whose schema declares `accepts`) shows a chevron and its children
 * indented beneath it with their own "Add block", and theme-fixed STATIC children (Gallery/Details) render
 * without a drag handle / delete (non-removable, non-reorderable). Drops are validated against each
 * container's `accepts` in the store. Block management lives ONLY here (founder ask 2026-06-25).
 */
function RailBlockTree({ section }: { section: SectionInstance }) {
  const def = sectionRegistry[section.type];
  const moveBlock = useBuilderStore((s) => s.moveBlock);

  // ONE DndContext for the whole block tree. Each level is its OWN SortableContext sharing this context
  // (dnd-kit's multi-container pattern). NESTED DndContexts (the old approach) let the outer context
  // swallow drags started in an inner level, which is why block reordering silently did nothing
  // (founder 2026-06-25). onDragEnd reorders the dragged block within its OWN level (same parent).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const a = findSiblingsDeep(section.blocks, String(active.id));
    const o = findSiblingsDeep(section.blocks, String(over.id));
    // Only reorder when both ends sit in the SAME level (same parent) — no cross-level jumps for now.
    if (!a || !o || a.parentId !== o.parentId) return;
    moveBlock(section.id, String(active.id), o.index);
  }

  return (
    <div className="bld-railblocks">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={flattenReorderableIds(section.blocks)}
          strategy={verticalListSortingStrategy}
        >
          <RailLevel
            section={section}
            blocks={section.blocks}
            parentBlockId={null}
            allowed={acceptsForSection(def)}
            depth={0}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
}

/**
 * One LEVEL of the block tree (a section's blocks, or a container block's children) — just the `<ul>` of
 * rows + the level's "Add block". Drag state is owned by the ONE flat SortableContext in RailBlockTree;
 * onDragEnd there keeps a reorder WITHIN the dragged block's own level (same parent). "Add block" offers
 * the level's accepted types — minus the section's static-child types, which are never user-addable.
 */
function RailLevel({
  section,
  blocks,
  parentBlockId,
  allowed,
  depth,
}: {
  section: SectionInstance;
  blocks: Block[];
  parentBlockId: string | null;
  allowed: string[];
  depth: number;
}) {
  const def = sectionRegistry[section.type];
  const blockName = (type: string) => {
    const raw = def?.schema.blocks?.find((b) => b.type === type)?.name;
    return raw ? blockDisplayName(section.type, type, raw) : type;
  };
  const staticTypes = staticChildTypes(def);
  const addable = parentBlockId == null ? allowed.filter((t) => !staticTypes.includes(t)) : allowed;

  // Rows belong to the SINGLE flat SortableContext owned by RailBlockTree (no per-level context — nested
  // contexts broke dragging).
  return (
    <>
      {blocks.length > 0 && (
        <ul role="list" className="bld-railblocks__items">
          {blocks.map((b, i) => (
            <Fragment key={b.id}>
              {/* Insert point ABOVE each non-static row → drop a block exactly between two existing ones. */}
              {addable.length > 0 && !b.static && (
                <RailInsertPoint
                  section={section}
                  parentBlockId={parentBlockId}
                  allowTypes={addable}
                  atIndex={i}
                />
              )}
              <RailBlockRow section={section} block={b} name={blockName(b.type)} depth={depth} />
            </Fragment>
          ))}
        </ul>
      )}
      {addable.length > 0 && (
        <RailAddBlock section={section} parentBlockId={parentBlockId} allowTypes={addable} />
      )}
    </>
  );
}

/**
 * One block row — RECURSIVE. A CONTAINER node (its schema declares `accepts`) shows a chevron and, when
 * expanded, its children indented beneath via a nested {@link RailLevel} (with their own "Add block").
 * A STATIC child (Gallery/Details) renders without a drag handle and without hide/delete (theme-fixed,
 * non-removable). Clicking the row opens that block's inspector on the right.
 */
function RailBlockRow({
  section,
  block,
  name,
  depth,
}: {
  section: SectionInstance;
  block: Block;
  name: string;
  depth: number;
}) {
  const def = sectionRegistry[section.type];
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const selectBlock = useBuilderStore((s) => s.selectBlock);
  const requestCanvasReveal = useBuilderStore((s) => s.requestCanvasReveal);
  const removeBlock = useBuilderStore((s) => s.removeBlock);
  const duplicateBlock = useBuilderStore((s) => s.duplicateBlock);
  const moveBlock = useBuilderStore((s) => s.moveBlock);
  const toggleBlockDisabled = useBuilderStore((s) => s.toggleBlockDisabled);
  const readOnly = isReadOnlyView();

  const childAllowed = acceptsForBlockType(def, block.type);
  const isContainer = childAllowed.length > 0;
  const isStatic = !!block.static;
  const [expanded, setExpanded] = useState(true);

  // This block's index within its OWN sibling level — for the Move up/down menu (the guaranteed,
  // non-drag reorder path). Static siblings are part of the array, so first/last are computed against
  // the reorderable (non-static) neighbours only.
  const sibs = findSiblingsDeep(section.blocks, block.id)?.siblings ?? [];
  const reorderable = sibs.filter((b) => !b.static);
  const myReorderIndex = reorderable.findIndex((b) => b.id === block.id);
  const myAbsIndex = sibs.findIndex((b) => b.id === block.id);
  const isFirstReorderable = myReorderIndex <= 0;
  const isLastReorderable = myReorderIndex === reorderable.length - 1;

  const sortable = useSortable({ id: block.id, disabled: isStatic || readOnly });
  const hidden = !!block.disabled;
  const selected = block.id === selectedBlockId;
  const accName = hidden ? `${name}, hidden` : name;
  const suffix = blockContentSuffix(block);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  function open() {
    selectSection(section.id);
    selectBlock(block.id);
    // Scroll the canvas to this block (Shopify parity) — the reveal effect targets the block node first,
    // falling back to its section. Fires after the two selects settle in one render.
    requestCanvasReveal();
  }

  return (
    <li ref={sortable.setNodeRef} style={style}>
      {/* The WHOLE row is the drag handle: the pointer `listeners` sit on the row container, not just the
          grip, so a seller can grab anywhere on the row to reorder (founder 2026-06-25). A click (no
          movement) still selects / toggles via the inner buttons — dnd-kit's 6px distance activation lets
          clicks through. Keyboard reordering stays available through the "⋯" Move up/down menu. */}
      <div
        className={selected ? 'bld-railblock is-selected' : 'bld-railblock'}
        data-rail-block-id={block.id}
        {...(isStatic || readOnly ? {} : sortable.listeners)}
        title={
          isStatic
            ? undefined
            : readOnly
              ? bt('chrome.view.readonly', 'Read-only consultation')
              : bt('chrome.rail.drag', 'Drag to reorder')
        }
      >
        {isContainer ? (
          <button
            type="button"
            className="bld-sec__chev dir-flip"
            aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDownIcon size={13} /> : <ChevronRightIcon size={13} />}
          </button>
        ) : (
          <span className="bld-sec__chev-spacer" aria-hidden="true" />
        )}
        <button
          type="button"
          className="bld-railblock__main"
          aria-current={selected ? 'true' : undefined}
          onClick={open}
        >
          <span
            className={hidden ? 'bld-railblock__icon is-hidden' : 'bld-railblock__icon'}
            aria-hidden="true"
          >
            {blockTypeIcon(isContainer ? 'group' : block.type)}
          </span>
          <span className={hidden ? 'bld-railblock__name is-hidden' : 'bld-railblock__name'}>
            {accName}
          </span>
          {suffix && (
            <span className="bld-railblock__suffix" aria-hidden="true">
              {suffix}
            </span>
          )}
        </button>
        {!isStatic && (
          <div className="bld-sec__actions">
            <button
              type="button"
              className="bld-sec__act"
              aria-label={hidden ? `Show ${name}` : `Hide ${name}`}
              aria-pressed={hidden}
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => toggleBlockDisabled(section.id, block.id)}
            >
              {hidden ? <EyeOffIcon size={14} /> : <EyeIcon size={14} />}
            </button>
            <button
              type="button"
              className="bld-sec__act bld-sec__act--danger"
              aria-label={btp('chrome.sec.delete_named', 'Delete {name}', { name })}
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => removeBlock(section.id, block.id)}
            >
              <TrashIcon size={14} />
            </button>
            <RowActionsMenu
              label={name}
              isFirst={isFirstReorderable}
              isLast={isLastReorderable}
              hidden={hidden}
              disabled={readOnly}
              onMoveUp={() => moveBlock(section.id, block.id, myAbsIndex - 1)}
              onMoveDown={() => moveBlock(section.id, block.id, myAbsIndex + 1)}
              onToggleHidden={() => toggleBlockDisabled(section.id, block.id)}
              onDuplicate={() => duplicateBlock(section.id, block.id)}
            />
          </div>
        )}
      </div>
      {isContainer && expanded && (
        <div className="bld-railsub__body">
          <RailLevel
            section={section}
            blocks={block.blocks ?? []}
            parentBlockId={block.id}
            allowed={childAllowed}
            depth={depth + 1}
          />
        </div>
      )}
    </li>
  );
}

/** Shopify-style block categories for the Add-block picker (the section's declared blocks, bucketed). */
const BLOCK_CATEGORY: Record<string, string> = {
  'media-gallery': 'Media',
  title: 'Product info',
  rating: 'Product info',
  price: 'Product info',
  'variant-picker': 'Product info',
  quantity: 'Product info',
  'buy-buttons': 'Buttons',
  description: 'Content',
  trust: 'Content',
  'collapsible-row': 'Content',
};
const BLOCK_CATEGORY_ORDER = ['Media', 'Product info', 'Buttons', 'Content', 'Other'];

/**
 * "Add block" — a Shopify-style FLOATING picker (shared {@link AddPopover}): the CONTAINER's accepted block
 * types (the Shrine `accepts` matrix) grouped into categories, max-aware (a type at its `max`, or the
 * section at `maxBlocks`, renders disabled). `parentBlockId` targets the container the new block lands in
 * (null = the section itself). Hidden when nothing is addable. Floating-over-canvas via portal so the
 * rail's `overflow:hidden` no longer clips it (founder feedback 2026-06-25).
 */
/**
 * The grouped, cap-aware "addable block" menu for a container level — shared by the bottom "Add block"
 * button and the between-row insert points. Pure (reads the module registry; no React hooks), so it can be
 * called from any render. Returns the AddPopover groups + whether anything is addable (group/section caps).
 */
function blockAddGroups(
  section: SectionInstance,
  parentBlockId: string | null,
  allowTypes: string[],
): { groups: AddPopoverGroup[]; anyAddable: boolean } {
  const def = sectionRegistry[section.type];
  const blockSchemas = (def?.schema.blocks ?? []).filter((bs) => allowTypes.includes(bs.type));
  const maxBlocks = def?.schema.maxBlocks;

  // Per-type caps count siblings in the TARGET container; the section cap counts the whole tree.
  const targetBlocks = parentBlockId
    ? (findBlockDeep(section.blocks, parentBlockId)?.blocks ?? [])
    : section.blocks;
  const counts: Record<string, number> = {};
  for (const b of targetBlocks) counts[b.type] = (counts[b.type] ?? 0) + 1;
  const sectionFull = maxBlocks != null && countBlocksDeep(section.blocks) >= maxBlocks;
  const atTypeMax = (bs: { type: string; max?: number }) =>
    bs.max != null && (counts[bs.type] ?? 0) >= bs.max;

  const anyAddable = blockSchemas.some((bs) => !sectionFull && !atTypeMax(bs));

  const byCat = new Map<string, AddPopoverGroup['items']>();
  for (const bs of blockSchemas) {
    const cat = BLOCK_CATEGORY[bs.type] ?? 'Other';
    const disabled = sectionFull || atTypeMax(bs);
    const arr = byCat.get(cat) ?? [];
    arr.push({
      type: bs.type,
      label: blockDisplayName(section.type, bs.type, bs.name),
      // Same reason as the section picker: the English name and the type id stay searchable.
      keywords: `${bs.name ?? ''} ${bs.type}`,
      icon: blockTypeIcon(bs.type),
      previewIcon: blockTypeIcon(bs.type, 28),
      disabled,
      disabledReason: sectionFull
        ? btp('chrome.sec.section_max', 'This section is limited to {max} blocks.', {
            max: maxBlocks ?? 0,
          })
        : atTypeMax(bs)
          ? btp('chrome.sec.type_max', 'You can add at most {max} {name}.', {
              max: bs.max ?? 0,
              name: blockDisplayName(section.type, bs.type, bs.name),
            })
          : undefined,
    });
    byCat.set(cat, arr);
  }
  const groups: AddPopoverGroup[] = BLOCK_CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => ({
    // The category NAME is the grouping key; only its DISPLAY is translated.
    group: bt(`chrome.railcat.${c.toLowerCase().replace(/ /g, '_')}`, c),
    items: byCat.get(c)!,
  }));
  return { groups, anyAddable };
}

/** The level's bottom "Add block" button — APPENDS the chosen block at the end of the container. */
function RailAddBlock({
  section,
  parentBlockId,
  allowTypes,
}: {
  section: SectionInstance;
  /** Container to add INTO (null = the section's own block list). */
  parentBlockId: string | null;
  /** The accepted child types offered by this container. */
  allowTypes: string[];
}) {
  const addBlock = useBuilderStore((s) => s.addBlock);
  const { groups, anyAddable } = blockAddGroups(section, parentBlockId, allowTypes);
  // Superadmin consultation: the SECTION pickers were greyed from the start, the BLOCK ones never were —
  // so the Header and Footer groups still opened a live picker and really inserted a block (founder
  // 25/10/2026). Same greyed trigger as AddSectionMenu; the store gate is the actual lock.
  const readOnly = isReadOnlyView();
  if (!anyAddable) return null;
  return (
    <div className="bld-railadd">
      <AddPopover
        groups={groups}
        onChoose={(type) => addBlock(section.id, type, undefined, parentBlockId)}
        triggerLabel={bt('chrome.rail.add_block', 'Add block')}
        triggerClassName="bld-add bld-add--inline"
        triggerDisabled={readOnly}
        triggerTitle={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
        searchPlaceholder={bt('chrome.rail.search_blocks', 'Search blocks')}
        menuAriaLabel={bt('chrome.rail.add_block_aria', 'Add a block')}
        emptyText={bt('chrome.rail.no_block_match', 'No blocks match “{q}”.')}
        previewHint={bt('chrome.rail.block_hover_hint', 'Hover a block to preview it.')}
        twoPane
      />
    </div>
  );
}

/**
 * A between-rows insert point (Shopify/Shrine parity): a thin hover zone that reveals a "+" line and opens
 * the same add-block menu, INSERTING the chosen block at this position (`atIndex`) instead of appending —
 * so a seller can drop a block exactly between two existing ones. The store splices at the clamped index.
 */
function RailInsertPoint({
  section,
  parentBlockId,
  allowTypes,
  atIndex,
}: {
  section: SectionInstance;
  parentBlockId: string | null;
  allowTypes: string[];
  atIndex: number;
}) {
  const addBlock = useBuilderStore((s) => s.addBlock);
  const { groups, anyAddable } = blockAddGroups(section, parentBlockId, allowTypes);
  // Not rendered in consultation — see RailSectionGroup.
  if (!anyAddable || isReadOnlyView()) return null;
  return (
    <li className="bld-railinsert">
      <AddPopover
        groups={groups}
        onChoose={(type) => addBlock(section.id, type, atIndex, parentBlockId)}
        triggerLabel=""
        triggerClassName="bld-railinsert__btn"
        triggerAriaLabel={bt('chrome.rail.add_block_here', 'Add a block here')}
        searchPlaceholder={bt('chrome.rail.search_blocks', 'Search blocks')}
        menuAriaLabel={bt('chrome.rail.add_block_aria', 'Add a block')}
        emptyText={bt('chrome.rail.no_block_match', 'No blocks match “{q}”.')}
        previewHint={bt('chrome.rail.block_hover_hint', 'Hover a block to preview it.')}
        twoPane
      />
    </li>
  );
}

/**
 * Non-drag fallback row menu (build-plan §7.4) so reorder + structural ops are reachable without
 * grabbing a handle (and keyboard-only via the menu). Each item dispatches one frozen reducer.
 * Generic — used by BOTH section rows and block rows (founder 2026-06-25: a guaranteed reorder path
 * that doesn't depend on drag-and-drop).
 */
function RowActionsMenu({
  label,
  isFirst,
  isLast,
  hidden,
  onMoveUp,
  onMoveDown,
  onToggleHidden,
  onDuplicate,
  disabled = false,
}: {
  label: string;
  isFirst: boolean;
  isLast: boolean;
  hidden: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleHidden: () => void;
  onDuplicate: () => void;
  /** Grey the ⋯ trigger without unmounting it (superadmin read-only consultation). */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function run(fn: () => void) {
    fn();
    setOpen(false);
    btnRef.current?.focus();
  }

  return (
    <div className="bld-rowmenu" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="bld-rowmenu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={btp('chrome.rail.actions_aria', '{name} actions', { name: label })}
        disabled={disabled}
        title={disabled ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div
          className="bld-rowmenu__panel"
          role="menu"
          aria-label={btp('chrome.rail.actions_aria', '{name} actions', { name: label })}
        >
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            disabled={isFirst}
            onClick={() => run(onMoveUp)}
          >
            Move up
          </button>
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            disabled={isLast}
            onClick={() => run(onMoveDown)}
          >
            Move down
          </button>
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            onClick={() => run(onDuplicate)}
          >
            Duplicate
          </button>
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            onClick={() => run(onToggleHidden)}
          >
            {hidden ? bt('chrome.rail.show', 'Show') : bt('chrome.rail.hide', 'Hide')}
          </button>
        </div>
      )}
    </div>
  );
}
